
import { useCore } from '@/composables/core/index.js'
import { getExternalId } from '@/lib/analytics/helpers.js'
import {
  EXTOLE_ELEMENT_ID,
  EXTOLE_ZONE_NAME,
  EXTOLE_CONTENT_ZONE_NAME,
  EXTOLE_CONTAINER,
} from './consts.js'
import { computed, type ComputedRef } from 'vue'
import {
  useReadyTrigger,
  useLogger,
} from '@booksy-com/vue-tools'
import axios, { type AxiosResponse } from 'axios'

type ExtoleData = {
  email: string
  first_name: string
  last_name: string
  locale: string
  partner_user_id: string | null
  labels: string
  region: string
}

type ExtoleStore = {
  state: {
    routingConfig: {
      config: {
        b2b_referral: boolean
        country: {
          code: string
        }
      }
    }
    identity: {
      user: {
        id: number
        email: string
        first_name: string
        last_name: string
      }
    }
  }
  getters: {
    'app/country': string
    'app/acceptLanguage': string
  }
}

type ExtoleUserService = {
  logout: () => void
}

type ExtoleApi = {
  tokenStore: {
    get: () => string
  }
  createZone: (
    payload: { name: string, element_id: string, data: ExtoleData },
    callback: (error?: unknown, zone?: unknown) => void
  ) => void
  require?: (dependencies: string[], callback: (userService: ExtoleUserService) => void) => void
}

type ExtoleWindow = Window & {
  extole?: ExtoleApi
}

type UseExtoleResult = {
  isExtoleB2BReferralsEnabled: ComputedRef<boolean>
  openReferralWindow: () => Promise<void>
  logout: () => Promise<void>
  getExtoleContent: () => Promise<AxiosResponse>
}

const getExtoleWindow = (): ExtoleWindow => window as ExtoleWindow

const logger = useLogger('extole')
const readyTrigger = useReadyTrigger()

const isExtoleB2BReferralsEnabled: ComputedRef<boolean> = computed(() => {
  const { store } = useCore()
  const typedStore = store as ExtoleStore

  return !typedStore.state.routingConfig.config.b2b_referral
  && typedStore.state.routingConfig.config.country.code === 'us'
})

const getExtoleContent = async (): Promise<AxiosResponse> => {
  const extoleWindow = getExtoleWindow()
  await _initialize()

  if (!extoleWindow.extole) {
    throw new Error('Extole is not initialized')
  }

  return axios({
    method: 'POST',
    url: 'https://refer.booksy.com/api/v6/zones',
    headers: {
      Authorization: `Bearer ${extoleWindow.extole.tokenStore.get()}`,
      'Content-Type': 'application/json',
    },
    data: {
      'event_name': EXTOLE_CONTENT_ZONE_NAME,
      data: _getExtoleData(),
    },
  })
}

const _initialize = async (): Promise<HTMLElement | null | void> => {
  if (isExtoleB2BReferralsEnabled.value) {
    const extoleWindow = getExtoleWindow()

    try {
      await _initializeExtoleScripts()
    } catch (e) {
      logger.error(e)
    }
    const extoleElement = _appendExtoleElement()

    const data = _getExtoleData()
    logger.debug('identifying to extole with data', data)

    return new Promise((resolve, reject) => {
      if (!extoleWindow.extole) {
        reject(new Error('Extole API unavailable'))
        return
      }

      extoleWindow.extole.createZone({
        name: EXTOLE_ZONE_NAME,
        element_id: EXTOLE_ELEMENT_ID,
        data,
      }, (error) => {
        if (error) {
          reject(error)
          return
        }
        logger.debug('extole initialized successfully')
        resolve(extoleElement)
      })
    })
  }
  logger.debug('skipping extole initialization - conditions not met')
}

const openReferralWindow = async (): Promise<void> => {
  const extoleElement = await _initialize()
  if (extoleElement) {
    logger.debug('opening extole window')
    extoleElement.click()
  }
}

const logout = async (): Promise<void> => {
  const extoleWindow = getExtoleWindow()

  if (extoleWindow.extole?.require && isExtoleB2BReferralsEnabled.value) {
    extoleWindow.extole.require(['core-root:///common/user-service.js'], (userService: ExtoleUserService) => {
      logger.debug('logging out from extole')
      userService.logout()
    })
  }
}

const _getExtoleData = (): ExtoleData => {
  const { store } = useCore()
  const typedStore = store as ExtoleStore
  const { email, first_name, last_name, id } = typedStore.state.identity.user
  const routingConfig = typedStore.state.routingConfig.config
  const country = typedStore.getters['app/country']
  const locale = typedStore.getters['app/acceptLanguage']
  const userId = getExternalId({ id, country, routingConfig }) || null
  const isProduction = window.location.hostname === 'booksy.com'
  const labels = isProduction ? EXTOLE_CONTAINER.PROD : EXTOLE_CONTAINER.STAGING
  const region = country.toUpperCase()

  return {
    email,
    first_name,
    last_name,
    locale,
    partner_user_id: userId,
    labels,
    region,
  }
}

const _initializeExtoleScripts = (): Promise<unknown> => {
  const extoleWindow = getExtoleWindow()

  if (!extoleWindow.extole) {
    const extoleScript = document.createElement('script')
    extoleScript.setAttribute('src', 'https://refer.booksy.com/core.js')
    extoleScript.async = true
    extoleScript.setAttribute('data-testid', 'extole-script')
    extoleScript.onload = () => {
      logger.debug('extole script ready')
      readyTrigger.resolveTrigger()
    }
    document.body.appendChild(extoleScript)

    const extoleZoneScript = document.createElement('script')
    extoleZoneScript.setAttribute('type', 'text/javascript')
    extoleZoneScript.setAttribute('data-testid', 'extole-zone-script')
    const innerExtoleZoneScript = `
      (function(c,b,f,k,a){c[b]=c[b]||{};for(c[b].q=c[b].q||[];a<k.length;)f(k[a++],c[b])})(window,"extole",function (c,b){b[c]=b[c]||function (){b.q.push([c,arguments])}},["createZone"],0)
    `
    extoleZoneScript.innerHTML = innerExtoleZoneScript
    document.body.appendChild(extoleZoneScript)
  }

  return readyTrigger.promise
}

const _appendExtoleElement = (): HTMLElement | null => {
  const extoleZoneWebCta = document.getElementById(EXTOLE_ELEMENT_ID)
  if (!extoleZoneWebCta) {
    const extoleElement = document.createElement('span')
    extoleElement.style.cssText = 'display:none;'
    extoleElement.id = EXTOLE_ELEMENT_ID
    document.body.appendChild(extoleElement)
    logger.debug('extole element created')
  }
  return document.getElementById(EXTOLE_ELEMENT_ID)
}

export function useExtole (): UseExtoleResult {
  return {
    isExtoleB2BReferralsEnabled,
    openReferralWindow,
    logout,
    getExtoleContent,
  }
}
